import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { AccommodationListItem } from '@models/accommodations';
import { CustomerSearchItem } from '@models/customers';
import { FishingSpotListItem } from '@models/fishing-spots';
import { ReservationDetail } from '@models/reservations';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeListsService } from '@services/codelists.service';
import { CrmAccommodationsService } from '@services/crm-accommodations.service';
import { CrmFishingSpotsService } from '@services/crm-fishing-spots.service';
import { CustomersService } from '@services/customers.service';
import { ReservationsService } from '@services/reservations.service';
import { ToastService } from '@services/toast.service';
import { customerSearch, displayCustomer, PickedCustomer, pickCustomer } from '@utils/customer-autocomplete';
import { Subscription } from 'rxjs';
import {
  buildSaveRequest,
  toAccommodationSeeds,
  toCoTravelerSeeds,
  toFishingSpotSeeds,
  toReservationPatch,
} from './reservation-form.mapper';
import { AccommodationForm, CoTravelerForm, FishingSpotForm, ReservationForm } from './reservation-form.model';
import {
  dateRange,
  noDuplicateAccommodations,
  noDuplicateCustomers,
  noDuplicateFishingSpots,
  requireSelection,
  swimRequiredWhenSelectable,
} from './reservation-form.validators';

@Component({
  selector: 'app-reservation-form',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    TranslatePipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
  templateUrl: './reservation-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReservationFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly reservationsService = inject(ReservationsService);
  private readonly customersService = inject(CustomersService);
  private readonly crmAccommodationsService = inject(CrmAccommodationsService);
  private readonly crmFishingSpotsService = inject(CrmFishingSpotsService);
  readonly codeListsService = inject(CodeListsService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private backUrl = '/reservations';

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly reservationId = signal<number | null>(null);
  readonly detail = signal<ReservationDetail | null>(null);
  // Picker options for the reservation's water, filtered to active ones.
  readonly accommodationOptions = signal<AccommodationListItem[]>([]);
  readonly fishingSpotOptions = signal<FishingSpotListItem[]>([]);
  // Booker autocomplete options (results of the customer search).
  readonly customerOptions = signal<CustomerSearchItem[]>([]);
  // Co-traveler customer-search results.
  readonly coTravelerOptions = signal<CustomerSearchItem[]>([]);

  // Per-row search subscriptions, torn down when a row is removed so a removed row can't keep
  // searching or overwrite the shared options with a stale in-flight result.
  private readonly coTravelerSearchSubs = new Map<FormGroup<CoTravelerForm>, Subscription>();
  // In-flight picker-option requests, cancelled before a new one is issued so a slow response for a
  // previously selected water can't arrive late and overwrite the options for the current water.
  private accommodationOptionsSub?: Subscription;
  private fishingSpotOptionsSub?: Subscription;

  readonly waterSearchQuery = signal('');
  readonly filteredWaters = computed(() => {
    const q = this.waterSearchQuery().toLowerCase().trim();
    const all = this.codeListsService.waters();
    return q ? all.filter((w) => w.name.toLowerCase().includes(q)) : all;
  });

  readonly form = new FormGroup<ReservationForm>({
    customer: new FormControl<PickedCustomer | string | null>(null, requireSelection),
    createdOn: new FormControl<Date | null>(null, Validators.required),
    siteId: new FormControl<number | null>(null, Validators.required),
    waterId: new FormControl<number | null>(null, Validators.required),
    statusId: new FormControl<number | null>(null, Validators.required),
    reservationType: new FormControl('private', { nonNullable: true }),
    companyName: new FormControl<string | null>(null),
    lastMinute: new FormControl(false, { nonNullable: true }),
    insurance: new FormControl(false, { nonNullable: true }),
    dog: new FormControl(false, { nonNullable: true }),
    dogBreed: new FormControl<string | null>(null),
    financial: new FormControl(false, { nonNullable: true }),
    retailPrice: new FormControl(0, { nonNullable: true, validators: Validators.required }),
    purchasePrice: new FormControl(0, { nonNullable: true, validators: Validators.required }),
    emergencyContact: new FormControl<string | null>(null),
    reservationComment: new FormControl<string | null>(null),
    coTravelers: new FormArray<FormGroup<CoTravelerForm>>([], noDuplicateCustomers),
    accommodations: new FormArray<FormGroup<AccommodationForm>>([], noDuplicateAccommodations),
    fishingSpots: new FormArray<FormGroup<FishingSpotForm>>([], noDuplicateFishingSpots),
  });

  // An exclusive-only water forces a whole-water booking and, in turn, permits an unset/dummy swim.
  // The backend flags this on the water codelist (mirrors App\Service\ReservationService).
  private readonly selectedWaterId = toSignal(this.form.controls.waterId.valueChanges, {
    initialValue: this.form.controls.waterId.value,
  });
  readonly isExclusiveWater = computed(
    () => this.codeListsService.waters().find((w) => w.id === this.selectedWaterId())?.exclusive ?? false,
  );

  // A swim must be picked only when the water has selectable (active, non-dummy) swims AND the row
  // itself is not whole-water. Since CS-228 the whole-water part is per row, so it is no longer a
  // property of the form as a whole — the validator reads it off the row being validated.
  readonly hasSelectableSwims = computed(() => this.fishingSpotOptions().length > 0);

  // Requires a swim only while the water offers any; a null value is valid on a whole-water row or a
  // water with no selectable swims. The rule lives in reservation-form.validators.
  private readonly swimRequired = swimRequiredWhenSelectable(() => this.hasSelectableSwims());

  // A FormArray is not reactive, so the effects below cannot observe rows being added or a row's
  // toggle flipping on their own. These derive signals from the array's valueChanges to depend on.
  private readonly fishingSpotsValue = toSignal(this.form.controls.fishingSpots.valueChanges, {
    initialValue: this.form.controls.fishingSpots.value,
  });
  private readonly fishingSpotRowCount = computed(() => this.fishingSpotsValue()?.length ?? 0);
  // Joined rather than an array so the computed's value compares by equality and only re-runs the
  // effect when a toggle actually changes. Disabled controls are absent from `value` and read as
  // false, which is fine — a locked toggle cannot be flipped by the user anyway.
  private readonly fishingSpotWholeWaterState = computed(() =>
    (this.fishingSpotsValue() ?? []).map((row) => (row.wholeWater === true ? '1' : '0')).join(''),
  );

  constructor() {
    // Keeps each swim row's whole-water lock and its "swim required" state consistent.
    //
    // An exclusive-only water forces whole-water on and locks it (legacy line 845), now on every swim
    // row rather than once for the reservation. Forcing it clears the row's swim requirement, so the
    // lock and the re-validation have to happen together: the forced write is silent (emitEvent:
    // false, or it would re-trigger this effect through the valueChanges it depends on), so a separate
    // validation effect would not observe it. Switching to an exclusive-only water that also has
    // selectable swims changes neither hasSelectableSwims() nor the toggle state signal, and the row
    // would keep a stale `required` error that nothing could clear.
    //
    // Note there is deliberately no collapse-to-one-row rule anymore. It existed because a single
    // reservation-level flag could only describe an all-or-nothing whole-water booking, so extra rows
    // were meaningless; per line, several rows are exactly the point — one renting the lake for a week
    // and the next taking a named swim. Spots are likewise not blanked when the toggle goes on: legacy
    // whole-water lines point at the dummy (and sometimes a real) swim, and the backend ignores the
    // swim on a whole-water line rather than rejecting it.
    effect(() => {
      const exclusive = this.isExclusiveWater();
      // Read every trigger: rows added/removed, a toggle flipped, or the swim options finishing load.
      this.fishingSpotRowCount();
      this.fishingSpotWholeWaterState();
      this.hasSelectableSwims();

      for (const group of this.fishingSpots.controls) {
        const ww = group.controls.wholeWater;
        if (exclusive) {
          if (!ww.value) ww.setValue(true, { emitEvent: false });
          if (ww.enabled) ww.disable({ emitEvent: false });
        } else if (ww.disabled) {
          ww.enable({ emitEvent: false });
        }

        // After the lock above, so a forced whole-water row drops its swim requirement in the same pass.
        group.controls.fishingSpotId.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? +idParam : null;
    this.reservationId.set(id);
    this.backUrl = this.route.snapshot.queryParamMap.get('backUrl') ?? '/reservations';

    // Booker search → query the existing customers endpoint (no list to preload; customers run into thousands).
    customerSearch(this.form.controls.customer, (term) => this.customersService.searchCustomers(term))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((items) => this.customerOptions.set(items));

    // Accommodation & fishing-spot picker options follow the selected water (also fires when populateForm patches it).
    this.form.controls.waterId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((waterId) => {
      this.loadAccommodationOptions(waterId);
      this.loadFishingSpotOptions(waterId);
    });

    if (id) {
      this.loading.set(true);
      this.reservationsService.getReservation(id).subscribe({
        next: (reservation) => {
          this.detail.set(reservation);
          this.populateForm(reservation);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.toast.showError(err);
          this.navigateToList();
        },
      });
    } else {
      // New reservation: default the booking date to today (matches the old CRM's required field).
      this.form.controls.createdOn.setValue(new Date());
    }
  }

  private populateForm(r: ReservationDetail): void {
    this.form.patchValue(toReservationPatch(r));

    // Rebuild the row arrays via the factories (which own the per-row search subscriptions).
    this.clearCoTravelers();
    for (const s of toCoTravelerSeeds(r)) {
      this.coTravelers.push(this.createCoTravelerFormGroup(s.id, s.customer, s.periodTargetId));
    }

    this.accommodations.clear();
    for (const s of toAccommodationSeeds(r)) {
      this.accommodations.push(
        this.createAccommodationFormGroup(
          s.id,
          s.accommodationId,
          s.fisherMan,
          s.nonFisherMan,
          s.arrivalDate,
          s.departureDate,
        ),
      );
    }

    this.fishingSpots.clear();
    for (const s of toFishingSpotSeeds(r)) {
      this.fishingSpots.push(
        this.createFishingSpotFormGroup(
          s.id,
          s.fishingSpotId,
          s.fisherMan,
          s.nonFisherMan,
          s.arrivalDate,
          s.departureDate,
          s.wholeWater,
        ),
      );
    }
  }

  addCoTraveler(): void {
    this.coTravelers.push(this.createCoTravelerFormGroup(null, null, null));
  }

  removeCoTraveler(index: number): void {
    const group = this.coTravelers.at(index);
    this.coTravelerSearchSubs.get(group)?.unsubscribe();
    this.coTravelerSearchSubs.delete(group);
    this.coTravelers.removeAt(index);
  }

  // Clears all co-traveler rows and tears down their search subscriptions.
  private clearCoTravelers(): void {
    this.coTravelerSearchSubs.forEach((sub) => {
      sub.unsubscribe();
    });
    this.coTravelerSearchSubs.clear();
    this.coTravelers.clear();
  }

  private createCoTravelerFormGroup(
    id: number | null,
    customer: PickedCustomer | null,
    periodTargetId: number | null,
  ): FormGroup<CoTravelerForm> {
    const group = new FormGroup<CoTravelerForm>({
      id: new FormControl<number | null>(id),
      customer: new FormControl<PickedCustomer | string | null>(customer, [Validators.required, requireSelection]),
      periodTargetId: new FormControl<number | null>(periodTargetId, Validators.required),
    });
    const sub = customerSearch(group.controls.customer, (term) => this.customersService.searchCustomers(term))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((items) => this.coTravelerOptions.set(items));
    this.coTravelerSearchSubs.set(group, sub);
    return group;
  }

  addAccommodation(): void {
    this.accommodations.push(this.createAccommodationFormGroup(null, null, null, null, null, null));
  }

  removeAccommodation(index: number): void {
    this.accommodations.removeAt(index);
  }

  private createAccommodationFormGroup(
    id: number | null,
    accommodationId: number | null,
    fisherMan: number | null,
    nonFisherMan: number | null,
    arrivalDate: Date | null,
    departureDate: Date | null,
  ): FormGroup<AccommodationForm> {
    return new FormGroup<AccommodationForm>(
      {
        id: new FormControl<number | null>(id),
        accommodationId: new FormControl<number | null>(accommodationId, Validators.required),
        fisherMan: new FormControl<number | null>(fisherMan, [Validators.required, Validators.min(0)]),
        nonFisherMan: new FormControl<number | null>(nonFisherMan, [Validators.required, Validators.min(0)]),
        arrivalDate: new FormControl<Date | null>(arrivalDate, Validators.required),
        departureDate: new FormControl<Date | null>(departureDate, Validators.required),
      },
      dateRange,
    );
  }

  // Loads the active accommodations for the given water into the picker options (edit-only feature).
  private loadAccommodationOptions(waterId: number | null): void {
    this.accommodationOptionsSub?.unsubscribe();
    if (waterId == null) {
      this.accommodationOptions.set([]);
      return;
    }
    this.accommodationOptionsSub = this.crmAccommodationsService.getAccommodations(waterId).subscribe({
      next: (items) => this.accommodationOptions.set(items.filter((a) => a.isActive)),
      error: () => this.accommodationOptions.set([]),
    });
  }

  addFishingSpot(): void {
    // A new row inherits the exclusive-only-water lock via the constructor effect, which re-runs when
    // the row count changes.
    this.fishingSpots.push(this.createFishingSpotFormGroup(null, null, null, null, null, null, false));
  }

  removeFishingSpot(index: number): void {
    this.fishingSpots.removeAt(index);
  }

  private createFishingSpotFormGroup(
    id: number | null,
    fishingSpotId: number | null,
    fisherMan: number | null,
    nonFisherMan: number | null,
    arrivalDate: Date | null,
    departureDate: Date | null,
    wholeWater: boolean,
  ): FormGroup<FishingSpotForm> {
    return new FormGroup<FishingSpotForm>(
      {
        id: new FormControl<number | null>(id),
        fishingSpotId: new FormControl<number | null>(fishingSpotId, this.swimRequired),
        fisherMan: new FormControl<number | null>(fisherMan, [Validators.required, Validators.min(0)]),
        nonFisherMan: new FormControl<number | null>(nonFisherMan, [Validators.required, Validators.min(0)]),
        arrivalDate: new FormControl<Date | null>(arrivalDate, Validators.required),
        departureDate: new FormControl<Date | null>(departureDate, Validators.required),
        wholeWater: new FormControl(wholeWater, { nonNullable: true }),
      },
      dateRange,
    );
  }

  // Loads the selectable fishing spots ("swims") for the given water into the picker options — active,
  // non-dummy spots only, matching the legacy FishingSpotRepository::findByWater. The dummy is never a
  // selectable option; an unset (dummy) swim is expressed by leaving the row's spot null.
  private loadFishingSpotOptions(waterId: number | null): void {
    this.fishingSpotOptionsSub?.unsubscribe();
    if (waterId == null) {
      this.fishingSpotOptions.set([]);
      return;
    }
    this.fishingSpotOptionsSub = this.crmFishingSpotsService.getSpots(waterId).subscribe({
      next: (items) => this.fishingSpotOptions.set(items.filter((s) => s.isActive && !s.isDummy)),
      error: () => this.fishingSpotOptions.set([]),
    });
  }

  onWaterPanelOpen(opened: boolean): void {
    if (opened) this.waterSearchQuery.set('');
  }

  onWaterSearch(event: Event): void {
    this.waterSearchQuery.set((event.target as HTMLInputElement).value);
  }

  private navigateToList(): void {
    this.router.navigateByUrl(this.backUrl);
  }

  cancel(): void {
    this.navigateToList();
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.toast.showError(this.translate.instant('reservations.form.invalid'));
      return;
    }

    const payload = buildSaveRequest(this.form.getRawValue());
    if (!payload) return;

    this.saving.set(true);
    const id = this.reservationId();
    const request$ = id
      ? this.reservationsService.updateReservation(id, payload)
      : this.reservationsService.createReservation(payload);

    request$.subscribe({
      next: () => {
        const key = id ? 'reservations.form.save-success-edit' : 'reservations.form.save-success-create';
        this.toast.showSuccess(this.translate.instant(key));
        this.navigateToList();
      },
      error: (err) => {
        this.saving.set(false);
        this.toast.showError(err);
      },
    });
  }

  protected readonly displayCustomer = displayCustomer;
  protected readonly pickCustomer = pickCustomer;

  get coTravelers(): FormArray<FormGroup<CoTravelerForm>> {
    return this.form.controls.coTravelers;
  }

  get accommodations(): FormArray<FormGroup<AccommodationForm>> {
    return this.form.controls.accommodations;
  }

  get fishingSpots(): FormArray<FormGroup<FishingSpotForm>> {
    return this.form.controls.fishingSpots;
  }

  get isBusiness(): boolean {
    return this.form.controls.reservationType.value === 'business';
  }
}
